import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Eye, EyeOff } from 'lucide-react';
import { API } from '../config';
import { ImageUpload } from './ImageUpload';

const playfair = "'Playfair Display', serif";
const inputStyle = { background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' };

const blankBanner = {
  title: '',
  subtitle: '',
  image_url: '',
  mobile_image_url: '',
  cta_label: '',
  cta_link: '',
  order: 0,
  active: true,
  starts_at: '',
  ends_at: '',
};

export const HeroBannersManager = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // banner being edited or 'new'
  const [form, setForm] = useState(blankBanner);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/hero-banners`, { withCredentials: true });
      setBanners(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const startCreate = () => { setForm(blankBanner); setEditing('new'); };
  const startEdit = (b) => { setForm({ ...b }); setEditing(b.id); };
  const cancel = () => { setEditing(null); setForm(blankBanner); };

  const save = async () => {
    if (!form.title || !form.image_url) {
      toast.error('Title and image are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, order: parseInt(form.order, 10) || 0 };
      if (editing === 'new') {
        await axios.post(`${API}/admin/hero-banners`, payload, { withCredentials: true });
      } else {
        await axios.put(`${API}/admin/hero-banners/${editing}`, payload, { withCredentials: true });
      }
      toast.success('Saved');
      cancel();
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this hero banner?')) return;
    try {
      await axios.delete(`${API}/admin/hero-banners/${id}`, { withCredentials: true });
      toast.success('Deleted');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div data-testid="hero-banners-manager">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-light" style={{ fontFamily: playfair, color: '#F8FAF9' }}>Hero Banners</h2>
        {!editing && (
          <button
            onClick={startCreate}
            className="px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2"
            style={{ background: '#D4AF37', color: '#080C0A' }}
            data-testid="hero-banner-new-btn"
          >
            <Plus className="w-4 h-4" /> New Banner
          </button>
        )}
      </div>

      {editing && (
        <div className="rounded-2xl p-5 mb-6 backdrop-blur-xl space-y-3" style={{ background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.2)' }}>
          <h3 className="text-sm uppercase tracking-wider" style={{ color: '#D4AF37' }}>{editing === 'new' ? 'New Banner' : 'Edit Banner'}</h3>
          <input type="text" placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle} data-testid="hero-banner-title-input" />
          <input type="text" placeholder="Subtitle" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle} />
          <ImageUpload value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} label="Desktop image *" testId="hero-banner-image-upload" required />
          <ImageUpload value={form.mobile_image_url} onChange={(v) => setForm({ ...form, mobile_image_url: v })} label="Mobile image (opsional, kalau berbeda)" testId="hero-banner-mobile-image-upload" />
          <div className="grid grid-cols-2 gap-3">
            <input type="text" placeholder="CTA Label (e.g. Explore)" value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} className="rounded-lg px-3 py-2.5 outline-none" style={inputStyle} />
            <input type="text" placeholder="CTA Link (e.g. /kuji)" value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} className="rounded-lg px-3 py-2.5 outline-none" style={inputStyle} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs mb-1" style={{ color: '#A3B8AC' }}>Mulai (opsional)</label>
              <input type="datetime-local" value={form.starts_at?.replace('Z','').slice(0,16) || ''} onChange={(e) => setForm({ ...form, starts_at: e.target.value ? new Date(e.target.value).toISOString() : '' })} className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs mb-1" style={{ color: '#A3B8AC' }}>Selesai (opsional)</label>
              <input type="datetime-local" value={form.ends_at?.replace('Z','').slice(0,16) || ''} onChange={(e) => setForm({ ...form, ends_at: e.target.value ? new Date(e.target.value).toISOString() : '' })} className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 items-center">
            <input type="number" placeholder="Order (lower = first)" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="rounded-lg px-3 py-2.5 outline-none" style={inputStyle} />
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer" style={{ background: 'rgba(0,0,0,0.2)' }}>
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-yellow-500" />
              <span className="text-sm" style={{ color: '#F8FAF9' }}>Active</span>
            </label>
          </div>
          <div className="flex gap-3">
            <button onClick={save} disabled={saving} className="px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50" style={{ background: '#D4AF37', color: '#080C0A' }} data-testid="hero-banner-save-btn">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={cancel} className="px-5 py-2.5 rounded-full text-sm" style={{ background: 'rgba(255,255,255,0.05)', color: '#F8FAF9' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ color: '#A3B8AC' }}>Loading...</p>
      ) : banners.length === 0 ? (
        <p style={{ color: '#A3B8AC' }}>No hero banners yet. Add one to display on the homepage carousel.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="hero-banners-list">
          {banners.map((b) => (
            <div key={b.id} className="rounded-xl overflow-hidden backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }} data-testid={`hero-banner-${b.id}`}>
              <div className="aspect-[16/7] relative" style={{ backgroundImage: `url(${b.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.1), rgba(0,0,0,0.7))' }} />
                <div className="absolute bottom-2 left-3 right-3">
                  <p className="font-medium truncate" style={{ color: '#F8FAF9' }}>{b.title}</p>
                  {b.subtitle && <p className="text-xs truncate" style={{ color: '#A3B8AC' }}>{b.subtitle}</p>}
                </div>
                <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-semibold flex items-center gap-1" style={{ background: b.active ? 'rgba(34,197,94,0.2)' : 'rgba(107,114,128,0.2)', color: b.active ? '#22C55E' : '#9CA3AF' }}>
                  {b.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  {b.active ? 'Live' : 'Hidden'}
                </span>
              </div>
              <div className="p-3 flex justify-between items-center text-xs">
                <span style={{ color: '#A3B8AC' }}>Order #{b.order}</span>
                <div className="flex gap-2">
                  <button onClick={() => startEdit(b)} className="px-3 py-1 rounded-full" style={{ background: 'rgba(212,175,55,0.15)', color: '#D4AF37' }} data-testid={`hero-banner-edit-${b.id}`}>Edit</button>
                  <button onClick={() => remove(b.id)} className="px-2 py-1 rounded-full" style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }} data-testid={`hero-banner-delete-${b.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
